import { Ban } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PAYMENT_METHOD_LABEL, partnerArApi, type PartnerPaymentRow } from "@/services/partnerAr";
import { formatCents } from "@/services/finance";
import { Pill } from "@/shared/ui/PageHeader";
import { ReasonCancelled, isReasonCancelled, useReasonDialog } from "@/shared/ui/ReasonDialog";

/**
 * REASON-2 · 收款记录（同行详情「订单 & 对账」）：每笔收款一行；有权限（finance.receivables.manage）的人每笔有「作废」按钮，
 * 点了弹必填原因（payment_void：录错金额 / 录错客户 / 款项退回 / 重复登记 / 其它）→ 反向冲回应收。已作废的显示原因，不再有按钮。
 */
export function PartnerPaymentsCard({ partnerId, canVoid }: { partnerId: string; canVoid: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["b2b", "payments", partnerId], queryFn: () => partnerArApi.payments(partnerId) });
  const { ask, dialog } = useReasonDialog();
  const voidMut = useMutation({
    mutationFn: async (pay: PartnerPaymentRow) => {
      const r = await ask({
        scene: "payment_void", title: "作废这笔收款", subtitle: `${pay.received_on} · ${formatCents(pay.amount_cents)} · ${PAYMENT_METHOD_LABEL[pay.method] ?? pay.method}`,
        danger: true, confirmText: "确认作废", changes: [{ label: "这笔收款", before: formatCents(pay.amount_cents), after: "作废（反向冲回应收）" }],
      });
      if (!r) throw new ReasonCancelled();
      return partnerArApi.voidPayment(partnerId, pay.id, r);
    },
    onSuccess: () => {
      for (const k of ["payments", "receivables", "credit", "partner"]) qc.invalidateQueries({ queryKey: ["b2b", k] });
    },
    onError: (e) => { if (!isReasonCancelled(e)) alert((e as Error).message || "作废失败，请重试"); },
  });
  const rows = q.data ?? [];
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid="payments-card">
      {dialog}
      <div className="mb-3 flex items-center gap-2">
        <h3 className="text-[15px] font-semibold text-slate-800">收款记录</h3>
        <span className="text-[12px] text-slate-400">登记错了可以作废（需写原因，应收会被冲回）</span>
      </div>
      {q.isLoading ? <div className="py-4 text-center text-[12px] text-slate-400">加载中…</div>
        : q.isError ? <div className="py-4 text-center text-[12px] text-slate-500">收款记录加载失败 <button type="button" onClick={() => q.refetch()} className="ml-2 text-blue-600 hover:underline">重试</button></div>
        : rows.length === 0 ? <div className="py-4 text-center text-[12px] text-slate-400">还没有收款记录</div>
        : (
          <div className="overflow-x-auto rounded-[10px] border border-slate-200">
            <table className="w-full min-w-[560px] text-[13px] tabular-nums">
              <thead className="bg-slate-50 text-[12px] font-medium text-slate-400">
                <tr><th className="px-2.5 py-2 text-left">收款日</th><th className="px-2.5 py-2 text-right">金额</th><th className="px-2.5 py-2 text-left">方式</th><th className="px-2.5 py-2 text-left">备注 / 单号</th><th className="px-2.5 py-2 text-left">状态</th><th className="px-2.5 py-2" /></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100" data-testid="payment-row">
                    <td className="px-2.5 py-2">{r.received_on}</td>
                    <td className={`px-2.5 py-2 text-right ${r.voided ? "text-slate-400 line-through" : ""}`}>{formatCents(r.amount_cents)}</td>
                    <td className="px-2.5 py-2">{PAYMENT_METHOD_LABEL[r.method] ?? r.method}</td>
                    <td className="px-2.5 py-2 text-slate-500">{r.reference ?? "—"}</td>
                    <td className="px-2.5 py-2">{r.voided ? <Pill tone="danger">{`已作废${r.void_reason ? ` · ${r.void_reason}` : ""}`}</Pill> : <Pill tone="success">有效</Pill>}</td>
                    <td className="px-2.5 py-2 text-right">
                      {canVoid && !r.voided && (
                        <button type="button" data-testid="payment-void-btn" disabled={voidMut.isPending} onClick={() => voidMut.mutate(r)}
                          className="inline-flex h-7 items-center gap-1 rounded-md border border-rose-200 bg-white px-2 text-[12px] font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50">
                          <Ban className="h-3 w-3" /> 作废
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </section>
  );
}
